import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
	AnimationContainer,
	AnimationImageAsset,
	AnimationImageElement,
	AnimationPositionElement,
	useAnimationContainerTimeline,
} from '../AnimationCommon';
import Functions from '@client/style/Functions';
import { Breakpoints, presetColors } from '@client/style/Variables';
import WaveContainer from '@client/modules/Dividers/WaveContainer';
import { Leaf } from '../AnimationElementLeaves';
import { AnimationElementLamps } from '../AnimationElementLamps';
import { gsap } from 'gsap';
import HazeSvg from '@client/assets/svgs/animations/haze.svg';

const Wrapper = styled.div`
	position: relative;
	width: 100%;
	display: grid;
	margin-bottom: -45%;
	overflow: hidden;

	${Functions.breakpoint(Breakpoints.mobile)} {
		margin-bottom: -23%;
	}

	&:has(+ .BreakerModule) {
		margin-bottom: 0;
	}
`;

const StyledAnimationContainer = styled(AnimationContainer)`
	img {
		max-width: 100%;
		height: auto;
	}
	background-color: ${presetColors.evergreenBright};
`;

const MobileContainer = styled.span`
	display: block;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: none;
	}
`;
const DesktopContainer = styled.span`
	display: none;
	${Functions.breakpoint(Breakpoints.mobile)} {
		display: block;
	}
`;
const HazeContainer = styled.div`
	width: 200vw;
	left: -10%;
	color: ${presetColors.blackish};

	svg {
		width: 100%;
		height: auto;
	}
`;

const LeafGroup = (props: { group: 1 | 2 | 3 | 4 | 5 | 6 }) => {
	const offsets = { groupOne: { x: 0, y: 0 }, groupTwo: { x: 25, y: 8 }, groupThree: { x: -60, y: 85 } };
	return (
		<>
			{props.group === 1 && (
				<>
					<Leaf
						delayOrder={2}
						variant={2}
						position={{ angle: -75, scale: 0.35, x: '-36%', y: '13%', breakpoints: { ultra: { scale: 0.55 } } }}
					></Leaf>
					<Leaf
						delayOrder={1}
						variant={2}
						position={{ angle: -40, scale: 0.35, x: '-39%', y: '17%', breakpoints: { ultra: { scale: 0.55 } } }}
					></Leaf>
					<Leaf
						variant={2}
						position={{ angle: -5, scale: 0.35, y: '19%', x: '-45%', breakpoints: { ultra: { scale: 0.55 } } }}
					></Leaf>
				</>
			)}
			{props.group === 2 && (
				<>
					<Leaf
						delayOrder={5}
						variant={2}
						position={{
							scale: 0.35,
							y: `${offsets.groupTwo.y - 7}%`,
							x: `${offsets.groupTwo.x - 9}%`,
							flip: true,
							angle: 75,
							anchor: 'right',
							breakpoints: { ultra: { scale: 0.55 } },
						}}
					></Leaf>
					<Leaf
						delayOrder={4}
						variant={2}
						position={{
							scale: 0.35,
							y: `${offsets.groupTwo.y - 3}%`,
							x: `${offsets.groupTwo.x - 6}%`,
							flip: true,
							angle: 40,
							anchor: 'right',
							breakpoints: { ultra: { scale: 0.55 } },
						}}
					></Leaf>
					<Leaf
						delayOrder={3}
						variant={2}
						position={{
							scale: 0.35,
							anchor: 'right',
							y: `${offsets.groupTwo.y}%`,
							x: `${offsets.groupTwo.x}%`,
							flip: true,
							angle: 5,
							breakpoints: { ultra: { scale: 0.55 } },
						}}
					></Leaf>
				</>
			)}
			{props.group === 3 && (
				<>
					<Leaf
						delayOrder={2}
						variant={2}
						position={{
							scale: 0.43,
							angle: -50,
							y: `${offsets.groupThree.y - 1}%`,
							x: `${offsets.groupThree.x - 2}%`,
							flipY: true,
							anchorY: 'bottom',
							anchor: 'center',
							breakpoints: { ultra: { scale: 0.65 } },
						}}
					></Leaf>
					<Leaf
						delayOrder={2.25}
						variant={2}
						position={{
							scale: 0.43,
							angle: -100,
							y: `${offsets.groupThree.y + 2}%`,
							anchorY: 'bottom',
							x: `${offsets.groupThree.x + 4}%`,
							anchor: 'center',
							breakpoints: { ultra: { scale: 0.65 } },
						}}
					></Leaf>
					<Leaf
						delayOrder={2}
						variant={2}
						position={{
							scale: 0.43,
							angle: -135,
							y: `${offsets.groupThree.y + 6}%`,
							x: `${offsets.groupThree.x + 2}%`,
							anchorY: 'bottom',
							anchor: 'center',
							breakpoints: { ultra: { scale: 0.65 } },
						}}
					></Leaf>
					<Leaf
						delayOrder={1.25}
						variant={2}
						position={{
							scale: 0.43,
							angle: -5,
							y: `${offsets.groupThree.y + 6}%`,
							x: `${offsets.groupThree.x}%`,
							flipY: true,
							anchorY: 'bottom',
							anchor: 'center',
							breakpoints: { ultra: { scale: 0.65 } },
						}}
					></Leaf>
				</>
			)}
			{props.group === 4 && (
				<>
					<Leaf variant={2} position={{ scale: 0.4, y: '92%', anchorY: 'bottom', x: '-42%', angle: -177 }}></Leaf>
					<Leaf variant={2} position={{ scale: 0.4, y: '101%', anchorY: 'bottom', x: '-40%', angle: -120 }}></Leaf>

					<Leaf variant={2} position={{ scale: 0.4, y: '94%', anchorY: 'bottom', x: '-46%', angle: -140 }}></Leaf>
				</>
			)}
			{props.group === 5 && (
				<>
					<Leaf
						variant={2}
						position={{ scale: 0.4, y: '91%', anchorY: 'bottom', anchor: 'right', x: '65%', angle: 150, flip: true }}
					></Leaf>
					<Leaf
						variant={2}
						position={{ scale: 0.4, y: '95%', anchorY: 'bottom', anchor: 'right', x: '65%', angle: 120, flip: true }}
					></Leaf>
				</>
			)}
			{props.group === 6 && (
				<>
					<Leaf
						variant={2}
						position={{ scale: 0.46, y: '-4%', anchor: 'right', x: '66%', angle: 20, flip: true }}
					></Leaf>
					<Leaf
						variant={2}
						position={{ scale: 0.45, y: '0%', anchor: 'right', x: '60%', angle: 50, flip: true }}
					></Leaf>
				</>
			)}
		</>
	);
};

const Speaker = () => {
	const speaker = new AnimationImageAsset('https://www.datocms-assets.com/80292/1686741760-megafon1.png', {
		mobile: 690,
		tiny: 400,
	});
	const imageRef = useRef<HTMLImageElement | null>(null);
	const containerTimeline = useAnimationContainerTimeline();

	useEffect(() => {
		gsap.set(imageRef.current, { transformOrigin: '100% 100%' });
		const tween = gsap.to(imageRef.current, {
			scale: 1.1,
			duration: 0.8,
			rotateZ: 5,
			x: -50,
			y: -10,
			yoyo: true,
			repeat: -1,
			ease: 'sine.inOut',
		});
		containerTimeline.add(tween, 0);
		return () => {
			tween.kill();
		};
	}, []);
	return <AnimationImageElement image={speaker} ref={imageRef}></AnimationImageElement>;
};
const Heart = (props: { variant: 1 | 2 }) => {
	const heart1Ref = React.useRef<HTMLImageElement>(null);
	const heart2Ref = React.useRef<HTMLImageElement>(null);

	const heart1 = new AnimationImageAsset('https://www.datocms-assets.com/80292/1679307288-hjerte-1.png', {
		tiny: 197,
		mobile: 300,
	});
	const heart2 = new AnimationImageAsset('https://www.datocms-assets.com/80292/1679307291-hjerte-2.png', {
		tiny: 205,
		mobile: 300,
	});

	const containerTimeline = useAnimationContainerTimeline();

	useEffect(() => {
		gsap.set('.heart', { transformOrigin: '100% 100%' });
		const tween = gsap.to('.heart', {
			scale: 1.1,
			duration: 0.8,
			rotateZ: 10,
			// x: -50,
			y: -20,
			yoyo: true,
			repeat: -1,
			ease: 'sine.inOut',
		});
		containerTimeline.add(tween, 0);
		return () => {
			tween.kill();
		};
	}, []);

	return (
		<>
			{props.variant === 1 && (
				<AnimationImageElement image={heart1} className={'heart'} ref={heart1Ref}></AnimationImageElement>
			)}
			{props.variant === 2 && (
				<AnimationImageElement image={heart2} className={'heart'} ref={heart2Ref}></AnimationImageElement>
			)}
		</>
	);
};

export function IllustrationSnakke() {
	return (
		<Wrapper>
			<WaveContainer side='top' addPadding='bottom'>
				<StyledAnimationContainer
					height={1200}
					width={690}
					breakpoints={{ mobile: { height: 2600, width: 4000 }, ultra: { width: 4000, height: 2500 } }}
				>
					<AnimationPositionElement position={{}}>
						<HazeContainer>
							<HazeSvg />
						</HazeContainer>
					</AnimationPositionElement>
					<DesktopContainer>
						<AnimationElementLamps
							variant={4}
							position={{
								y: '7%',
								x: '-50%',
								scale: 1.2,
								breakpoints: { mobile: { y: '0%', x: '-30%', scale: 0.45 }, ultra: { scale: 0.65, y: '10%' } },
							}}
						/>
					</DesktopContainer>
					<AnimationElementLamps
						variant={1}
						position={{
							flip: true,
							y: '150%',
							x: '-55%',
							angle: -20,
							scale: 2.2,
							breakpoints: {
								mobile: { y: '10%', x: '15%', scale: 0.8, angle: 0 },
								ultra: { scale: 1.1, y: '65%', x: '20%' },
							},
						}}
					/>
					<AnimationElementLamps
						variant={5}
						position={{
							y: '0%',
							x: '-5%',
							angle: -20,
							scale: 1.4,
							anchor: 'right',
							breakpoints: {
								mobile: { y: '5%', x: '30%', scale: 0.45, angle: 0 },
								ultra: { scale: 0.75, x: '20%', y: '20%' },
							},
						}}
					/>
					<DesktopContainer>
						<AnimationElementLamps
							variant={2}
							position={{
								flip: true,
								y: '60%',
								x: '-47%',
								scale: 0.75,
								anchor: 'right',
								breakpoints: { ultra: { scale: 1.0, y: '140%', x: '-80%' } },
							}}
						/>
					</DesktopContainer>

					<DesktopContainer>
						<LeafGroup group={1} />
						<LeafGroup group={2} />
					</DesktopContainer>

					<AnimationPositionElement
						position={{
							anchor: 'center',
							y: '105%',
							width: '55vw',
							x: '-10%',
							breakpoints: {
								mobile: { y: '105%', width: '20vw', x: '0' },
								ultra: {
									y: '130%',
								},
							},
						}}
					>
						<Speaker />
					</AnimationPositionElement>
					<AnimationPositionElement
						position={{
							anchor: 'center',
							y: '550%',
							angle: -25,
							x: '-180%',
							width: '14vw',
							breakpoints: { mobile: { y: '580%', angle: -25, x: '-140%', width: '5vw' }, ultra: { y: '530%' } },
						}}
					>
						<Heart variant={2} />
					</AnimationPositionElement>
					<AnimationPositionElement
						position={{
							flip: true,
							width: '10vw',
							anchor: 'center',
							y: '420%',
							angle: -25,
							x: '-290%',
							breakpoints: { mobile: { y: '550%', x: '-250%', width: '3vw' }, ultra: { y: '500%' } },
						}}
					>
						<Heart variant={1} />
					</AnimationPositionElement>
					<AnimationPositionElement
						position={{
							width: '15vw',
							anchor: 'center',
							y: '180%',
							x: '-140%',
							angle: 25,
							breakpoints: { mobile: { y: '200%', x: '-50%', width: '6vw' }, ultra: { y: '170%' } },
						}}
					>
						<Heart variant={1} />
					</AnimationPositionElement>
					<MobileContainer>
						<LeafGroup group={4} />
						<LeafGroup group={5} />
						<LeafGroup group={6} />
					</MobileContainer>
					<DesktopContainer>
						<LeafGroup group={3} />
					</DesktopContainer>
				</StyledAnimationContainer>
			</WaveContainer>
		</Wrapper>
	);
}
